import React, { useEffect, useState } from "react";
import { ChevronDown, Plus, Radio, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuLabel
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function StationSwitcher() {
  const { user, checkAuth } = useAuth();
  const [stations, setStations] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", genre: "", tagline: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/stations");
      setStations(res.data);
    } catch {}
  };

  useEffect(() => { load(); }, [user?.station_id]);

  const switchTo = async (sid) => {
    if (sid === user?.station_id) return;
    try {
      await api.post(`/stations/switch/${sid}`);
      // Persist the new station in localStorage so the reload paints
      // the correct identity instantly (avoids a flicker of the previous
      // station's branding while /auth/me round-trips).
      try { localStorage.setItem("bf_active_station_id", sid); } catch { /* storage may be blocked */ }
      toast.success("Switched station — reloading workspace…");
      // Fire the legacy event for anything that listens (e.g. Dashboard)
      window.dispatchEvent(new Event("station-switched"));
      // Hard reload: guarantees every page (Music Studio, News, Podcasts,
      // Commercials, Imaging, Vision, Avatar, etc.) re-fetches its own
      // station-scoped data. Matches the Stripe / GitHub / Linear pattern
      // for organisation/workspace switching. ~200ms hard refresh.
      setTimeout(() => { window.location.reload(); }, 250);
    } catch { toast.error("Failed to switch"); }
  };

  const create = async () => {
    if (!form.name.trim()) { toast.error("Station name required"); return; }
    setSaving(true);
    try {
      const res = await api.post("/stations", form);
      const newId = res?.data?.station_id;
      // Auto-switch to the just-created station so the workspace context
      // matches what the user expects after hitting "Launch".
      if (newId) {
        try { await api.post(`/stations/switch/${newId}`); } catch { /* non-fatal */ }
        try { localStorage.setItem("bf_active_station_id", newId); } catch { /* storage may be blocked */ }
      }
      toast.success("Station launched — loading workspace…");
      setShowCreate(false);
      setForm({ name: "", genre: "", tagline: "" });
      window.dispatchEvent(new Event("station-switched"));
      setTimeout(() => { window.location.reload(); }, 250);
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  const active = stations.find((s) => s.station_id === user?.station_id);

  if (stations.length === 0) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="station-switcher"
            className="inline-flex items-center gap-2 rounded-md border border-line bg-bg-surface px-3 py-1.5 text-sm hover:border-brand-amber/50 transition"
          >
            <Radio className="h-3.5 w-3.5 text-brand-amber" />
            <span className="font-medium max-w-[110px] sm:max-w-[180px] truncate">{active?.name || "Select station"}</span>
            <ChevronDown className="h-3.5 w-3.5 text-ink-muted" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="bg-bg-elevated border-line text-ink-primary w-64" align="end">
          <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">
            Your stations ({stations.length})
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-line" />
          {stations.map((s) => (
            <DropdownMenuItem
              key={s.station_id}
              onClick={() => switchTo(s.station_id)}
              data-testid={`switcher-item-${s.station_id}`}
              className="flex items-center justify-between cursor-pointer hover:text-cyan-300 focus:text-cyan-300"
            >
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{s.name}</div>
                <div className="text-[10px] text-ink-muted font-mono">{s.genre || "—"}</div>
              </div>
              {s.station_id === user?.station_id && <Check className="h-3.5 w-3.5 text-brand-amber" />}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator className="bg-line" />
          <DropdownMenuItem
            onClick={() => setShowCreate(true)}
            data-testid="switcher-add-btn"
            className="cursor-pointer text-brand-amber hover:text-cyan-300 focus:text-cyan-300"
          >
            <Plus className="h-3.5 w-3.5 mr-2" /> Launch new station
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-bg-surface border-line text-ink-primary" data-testid="switcher-create-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Launch new station</DialogTitle>
            <DialogDescription className="text-ink-secondary">
              Run multiple stations from one dashboard. Switch anytime.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-ink-secondary">Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-bg border-line" data-testid="switcher-create-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-ink-secondary">Genre</Label>
              <Input value={form.genre} onChange={(e) => setForm({ ...form, genre: e.target.value })} className="mt-1 bg-bg border-line" data-testid="switcher-create-genre" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-ink-secondary">Tagline</Label>
              <Input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className="mt-1 bg-bg border-line" data-testid="switcher-create-tagline" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={create} disabled={saving} data-testid="switcher-create-submit" className="rounded-md bg-brand-amber px-5 py-2 font-semibold text-black amber-glow disabled:opacity-50">
              {saving ? "Launching..." : "Launch"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
